import MainLayout from "../../../layouts/MainLayout";
import "../../../assets/styles/custom-styles.css";
import Form from "../../../components/forms/Form";
import PageHeader from "../../../components/PageHeader";
import SectionHeader from "../../../components/forms/SectionHeader";
import SimpleBody from "../../../components/forms/SimpleBody";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { useLocation, useNavigate } from "react-router-dom";
import useLoader from "../../../hooks/useLoader";
import useNotification from "../../../hooks/useNotification";
import useBaseService from "../../../hooks/services/useBaseService";
import {
  normalizeFormErrors,
  setDefaultFieldValues,
} from "../../../utils/objectUtils";
import { transformFiltersForUrl } from "../../../utils/transformFiltersForUrl";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { entities } from "../../../constants/entities";
import useForm from "../../../hooks/useForm";
import { validateItensShipmentFields } from "../../../constants/forms/shipmentFields";
import { Box, Grid } from "@mui/material";
import useLocalStorage from "../../../hooks/useLocalStorage";
import useRemoteSelectOptions from "../../../hooks/useRemoteSelectOptions";
import ValidateShipmentTable from "./components/ValidateShipmentTable";
import ValidateShipmentsInput from "./components/ValidateShipmentsInput";
import VolumeValidationModal from "./components/VolumeValidationModal";
import useQueryWithLoader from "../../../hooks/useQueryWithLoader";
import { GenericStatusEnum } from "../../../enums/generic-status-enum";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import RomaneioModal from "./components/RomaneioModal";

dayjs.extend(customParseFormat);

const ValidateShipmentsPage = () => {
  const isVolumeAttended = (status) =>
    status === 3 ||
    status === "3" ||
    (typeof status === "string" &&
      status.trim().toUpperCase() === "TOTALMENTE ATENDIDO");
  const navigate = useNavigate();
  const location = useLocation();
  const { showNotification } = useNotification();
  const { showLoader, hideLoader, isLoading } = useLoader();
  const inputRefVolume = useRef(null);
  const queryParams = new URLSearchParams(location.search);
  const movementIdFromUrl = queryParams.get("movement_id");

  // * Usara para adicionar volumes a esse carregamento
  const addVolumeShipmentId = queryParams.get("shipment_id");

  // Referência para evitar loops, igual ao contractRef do outro arquivo
  const movementRef = useRef(movementIdFromUrl);

  const {
    save: saveMovementIdToLocalStorage,
    load: loadMovementIdFromLocalStorage,
  } = useLocalStorage("selectedMovementIds");

  const [validateItensShipmentSection] = validateItensShipmentFields;

  // Serviços
  const {
    post: baseServicePost,
    get: baseServiceGet,
    formErrors,
  } = useBaseService(navigate);

  const dynamicSection = validateItensShipmentSection;

  const { formData, handleChange, setFormData } = useForm(
    setDefaultFieldValues([dynamicSection]),
    [dynamicSection],
  );

  // Estados
  const [numberVolumeInput, setNumberVolumeInput] = useState("");
  const [shipmentReceivedVolumes, setShipmentReceivedVolumes] = useState([]);
  const [volumeValidation, setVolumeValidation] = useState({
    open: false,
    volume: null,
  });
  const [notificationMessage, setNotificationMessage] = useState(null);
  const [notificationStatus, setNotificationStatus] = useState("");
  const [filterDescriptionVolume, setFilterDescriptionVolume] = useState("");
const [romaneioModalOpen, setRomaneioModalOpen] = useState(false);
  const [createdShipmentId, setCreatedShipmentId] = useState(null);
  const [pendingShipmentData, setPendingShipmentData] = useState(null);
  const shipmentDoesNotRequireInvoiceRef = useRef(false);

  // Efeito para notificações de rota
  useEffect(() => {
    if (location.state?.message) {
      showNotification(location.state.type, location.state.message);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, showNotification, navigate, location.pathname]);

  /**
   * Limpa os estados de notificação.
   */
  const clearNotification = useCallback(() => {
    setNotificationMessage(null);
    setNotificationStatus("");
  }, []);

  // Efeito para disparar as notificações quando a mensagem ou severidade mudam
  useEffect(() => {
    if (notificationMessage) {
      showNotification(notificationStatus, notificationMessage);
      clearNotification();
    }
  }, [
    clearNotification,
    notificationMessage,
    notificationStatus,
    showNotification,
  ]);

  // Somente quando for mandar o formData
  const movementIds = formData.movement?.id;

  const selectedMovementIds = useMemo(
    () => (movementIds || []).map((m) => m?.value ?? m),
    [movementIds]
  );

  const movementCountRef = useRef(movementIds?.length);
  useEffect(() => {
    if (movementCountRef.current !== movementIds?.length) {
      movementCountRef.current = movementIds?.length;
      saveMovementIdToLocalStorage([{ id: movementIds }]);
    }
  }, [movementIds, movementIds?.length, saveMovementIdToLocalStorage]);

  const {
    save: saveReceivedVolumesToLocalStorage,
    load: loadReceivedVolumesFromLocalStorage,
    clear: clearReceivedVolumesFromLocalStorage,
  } = useLocalStorage(`shipmentReceivedVolumes_default`);

  // UseEffect para carregar o ID do movimento salvo no localStorage
  useEffect(() => {
    const storedIds = loadMovementIdFromLocalStorage();
    if (storedIds && storedIds.length > 0) {
      // restaura o que estava no storage
      setFormData((prev) => ({
        ...prev,
        movement: {
          ...prev.movement,
          id: storedIds[0],
        },
      }));
    }
  }, [loadMovementIdFromLocalStorage, setFormData]);

  // Itens recebidos (LocalStorage)
  const { data: receivedItemsData, isError: isErrorReceivedItems } =
    useQueryWithLoader({
      queryKey: ["shipmentReceivedItems", movementIds?.length > 0],
      queryFn: async () => {
        const storedVolumesIds = loadReceivedVolumesFromLocalStorage() || [];

        const volumePromises = storedVolumesIds
          .map((volumeId) =>
            volumeId
              ? baseServiceGet(entities.volumes.getById(volumeId))
              : null,
          )
          .filter(Boolean);

        const volumesResponses = await Promise.all(volumePromises);

        const receivedVolumes = volumesResponses
          .map((res) => res?.result)
          .filter(Boolean)
          .map((volume) => ({
            id: volume.id,
            description: `${volume.code || "-"} - ${volume.name || "-"}`,
            movement_id: volume.movement_id,
            items: volume.items?.map((item) => ({
              type: item.product_item_id ? "product" : "equipment",
              movement_item_id: item.movement_item_id,
              ...(item.product_item_id !== null && {
                product_item_id: item.product_item_id,
              }),

              ...(item.equipment_id !== null && {
                equipment_id: item.equipment_id,
              }),
              quantity: item.quantity,
            })),
          }));

        return {
          volumes: receivedVolumes,
        };
      },
      enabled: movementIds?.length > 0,
      staleTime: 0,
    });

  // Efeito para carregar itens recebidos ao montar o componente
  useEffect(() => {
    if (receivedItemsData) {
      setShipmentReceivedVolumes(receivedItemsData.volumes);
    } else if (isErrorReceivedItems) {
      setShipmentReceivedVolumes([]);
    }
  }, [receivedItemsData, isErrorReceivedItems]);

  // Buscar todoas as orderm de serviço
  const movementSelect = useRemoteSelectOptions({
    queryKey: ["movement.id", movementIdFromUrl],
    fetcher: async ({ page = 1, perPage = 15, search = "" }) => {
      const idFilter = movementIdFromUrl || search;
      const filters = transformFiltersForUrl({
        deleted_at: false,
        maintenance_created_movement: false,
        status_id: [
          GenericStatusEnum.NOT_ATTENDED,
          GenericStatusEnum.PARTIALLY_ATTENDED,
        ], // NÃO ATENDIDO | PARCIALMENTE ATENDIDO
        ...(idFilter && { id: idFilter }),
      });
      const params = { filters, page, perPage };
      const response = await baseServiceGet(entities.movements.get, params);
      return response.result;
    },
    fieldId: "movement.id",
    formData,
    isMulti: true,
    mapToOption: (movement) => {
      const movementType = movement.movement_type?.name
      const movementTypeLabel =
        movementType === "DEVOLUÇÃO" ? "RETORNO" : movementType ?? "-"
      const customerAlias = movement.recipient_customer?.alias ?? "-"
      const destinationName = movement.order?.destination_order?.name

      return {
        value: movement.id,
        label: `M#${movement.number} -- ${customerAlias} - ${movementTypeLabel}${
          movement.order?.destination_order_id && destinationName
            ? ` - ${destinationName}`
            : ""
        }`,
      }
    },
    setFormData,
    perPage: 15,
    initialPage: 1,
    enablePagination: true,
    immediateFetch: true,
  });

  const { data: expectedVolumesData } = useQueryWithLoader({
    queryKey: ["expectedVolumes", selectedMovementIds],
    queryFn: async () => {
      const filters = {
        movement_id: selectedMovementIds,
      };
      const filtersTransformed = transformFiltersForUrl(filters);
      // * `view: "lean"` evita carregar as relations pesadas que essa listagem
      // * não usa (os itens do volume são buscados sob demanda no botão Detalhes).
      const params = {
        filters: filtersTransformed,
        page: 1,
        perPage: 0,
        view: "lean",
      };
      const response = await baseServiceGet(
        entities.movementItemsVolumes.get,
        params,
      );
      return response.result.data;
    },
    select: (data) =>
      data?.map((item) => ({
        id: item.id,
        movement_id: item.movement_id,
        description: `${item.code} - ${item.name}` || "-",
        status: item.status || "-",
        items_count: item.items_count ?? 0,
        total_quantity: item.total_quantity ?? 0,
      })),
    enabled: selectedMovementIds.length > 0,
    staleTime: 0,
    refetchOnMount: true,
  });

  const selects = useMemo(
    () => ({
      "movement.id": movementSelect,
    }),
    [movementSelect],
  );

  const queryClient = useQueryClient();

const createShipmentMutation = useMutation({
  mutationFn: async (data) => {
    showLoader();
    const response = await baseServicePost(entities.shipments.create, data, {
      skipNotification: true,
    });
    return response;
  },
  onSuccess: async (data) => {
    const httpStatus = Number(data?.status);
    const isOk =
      httpStatus === 201 ||
      httpStatus === 200 ||
      data?.status === "created" ||
      data?.status === "success";

    if (!isOk) {
      shipmentDoesNotRequireInvoiceRef.current = false;
      console.warn("❌ Create shipment: status inesperado", data);
      showNotification("error", "Erro ao criar carregamento.");
      return;
    }

    // Busca o shipmentId
    let shipmentId =
      data?.result?.id ??
      data?.result?.shipment?.id ??
      data?.result?.data?.id ??
      data?.data?.result?.id ??
      data?.data?.id ??
      data?.id ??
      null;

    // Invalida queries
    queryClient.invalidateQueries({ queryKey: ["shipments"] });
    queryClient.invalidateQueries({ queryKey: ["movementShipments"] });
    queryClient.invalidateQueries({ queryKey: ["movements"] });
    queryClient.invalidateQueries({ queryKey: ["movementItems"] });
    queryClient.invalidateQueries({ queryKey: ["expectedShipmentItems"] });
    queryClient.invalidateQueries({ queryKey: ["expectedVolumes"] });
    queryClient.invalidateQueries({ queryKey: ["contractDetails"] });
    queryClient.invalidateQueries({ queryKey: ["contractItems"] });

    if (!shipmentId) {
      console.error("❌ ShipmentId não encontrado", data);
      showNotification("warning", "Carregamento criado, mas não foi possível abrir o romaneio automaticamente.");
      clearReceivedVolumesFromLocalStorage();
      navigate("/aluguel/carregamentos", { replace: true });
      return;
    }

    // 🆕 Se não precisa de NF, pula o romaneio e navega direto
    if (
      data?.result?.requires_invoice === false ||
      shipmentDoesNotRequireInvoiceRef.current
    ) {
      shipmentDoesNotRequireInvoiceRef.current = false;
      console.log('✅ Sem NF obrigatória — pulando romaneio');
      clearReceivedVolumesFromLocalStorage();
      showNotification('success', 'Carregamento criado com sucesso.');
      navigate("/aluguel/carregamentos", { replace: true });
      return;
    }

    // Abre o romaneio normalmente quando precisa de NF
    setCreatedShipmentId(shipmentId);
    setRomaneioModalOpen(true);
    clearReceivedVolumesFromLocalStorage();
    showNotification("success", "Carregamento criado com sucesso.");
  },
  onError: (error) => {
    console.error('❌ [onError] mutation falhou:', error);
    showNotification("error", error?.message || "Erro ao criar carregamento.");
  },
  onSettled: () => {
    hideLoader();
  },
});

  const addVolumeInShipmentMutation = useMutation({
    mutationFn: async (data) => {
      showLoader();
      const response = await baseServicePost(
        entities.shipments.volumes.addVolume,
        data,
      );
      return response;
    },
    onSuccess: (data) => {
      if (data?.status === 200) {
        clearReceivedVolumesFromLocalStorage();
        // Invalida ShipmentsPage
        queryClient.invalidateQueries({ queryKey: ["shipments"] });
        queryClient.invalidateQueries({
          queryKey: ["movementShipments"],
        });
        queryClient.invalidateQueries({
          queryKey: ["movements"],
        });
        queryClient.invalidateQueries({
          queryKey: ["movementItems"],
        });
        queryClient.invalidateQueries({
          queryKey: ["expectedShipmentItems"],
        });
        queryClient.invalidateQueries({
          queryKey: ["expectedVolumes"],
        });
        queryClient.invalidateQueries({ queryKey: ["contractDetails"] });
        queryClient.invalidateQueries({ queryKey: ["contractItems"] });
        queryClient.invalidateQueries({ queryKey: ["shipmentItemVolumes"] });

        navigate(`/aluguel/carregamentos/${addVolumeShipmentId}/detalhes`, {
          replace: true,
        });
      }
    },
    onSettled: () => {
      hideLoader();
    },
  });

const handleSubmit = async () => {
    try {
      if (shipmentReceivedVolumes.length === 0) {
        showNotification("warning", "Nenhum volume foi adicionado.");
        return;
      }

      // Se for adicionar volume a um carregamento existente, cria diretamente
      if (addVolumeShipmentId) {
        const payload = {
          shipment: {
            id: addVolumeShipmentId,
            movement_ids: movementIds?.map((item) => item.value),
          },
          volumes: shipmentReceivedVolumes?.map((item) => ({
            volume_id: item.id,
            movement_id: item.movement_id,
            items: item.items,
          })),
        };

        await addVolumeInShipmentMutation.mutateAsync(payload);
        return;
      }

      // O campo requires_invoice vem do movimento; confirme todos os
      // selecionados antes de decidir se o romaneio deve ser omitido.
      let selectedMovementData = [];
      try {
        selectedMovementData = await Promise.all(
          selectedMovementIds.map(async (id) => {
            const response = await baseServiceGet(entities.movements.getById(id));
            return response?.result;
          }),
        );
      } catch (error) {
        // If lookup fails, keep the existing safe flow and show the modal.
        console.warn("Não foi possível verificar a exigência de NF:", error);
      }
      const allMovementsDoNotRequireInvoice =
        selectedMovementIds.length > 0 &&
        selectedMovementData.length === selectedMovementIds.length &&
        selectedMovementData.every((movement) => {
          const requiresInvoice =
            movement?.requires_invoice ??
            movement?.service_order?.requires_invoice ??
            movement?.order?.requires_invoice;
          return (
            requiresInvoice === false ||
            requiresInvoice === "false" ||
            requiresInvoice === 0 ||
            requiresInvoice === "0"
          );
        });

      const shipmentData = {
        shipment: {
          movement_ids: movementIds?.map((item) => item.value),
        },
        volumes: shipmentReceivedVolumes?.map((item) => ({
          volume_id: item.id,
          movement_id: item.movement_id,
          items: item.items,
        })),
      };

      if (allMovementsDoNotRequireInvoice) {
        shipmentDoesNotRequireInvoiceRef.current = true;
        await createShipmentMutation.mutateAsync(shipmentData);
        return;
      }

      // Para novo carregamento com NF, abrir o modal do romaneio primeiro.
      setPendingShipmentData(shipmentData);
      setRomaneioModalOpen(true);
    } catch (error) {
      shipmentDoesNotRequireInvoiceRef.current = false;
      console.error("Erro ao criar o carregamento:", {
        status: error.status,
        message: error.message,
      });
    }
  };

  const getOptions = useCallback(
    (fieldId) => {
      const selectConfig = selects[fieldId];
      return selectConfig?.options || [];
    },
    [selects],
  );

  const getSelectedValue = useCallback(
    (fieldId) => {
      const selectConfig = selects[fieldId];
      return selectConfig?.selectedValue || null;
    },
    [selects],
  );

  const addVolumeToReceived = useCallback(
    async (volumeCode) => {
      if (volumeCode.trim() === "") return;

      showLoader();
      try {
        const payload = {
          codes: [volumeCode],
        };

        const response = await baseServicePost(
          entities.volumes.getByCode,
          payload,
          { skipNotification: true },
        );
        const volumes = response?.result;

        if (!Array.isArray(volumes) || volumes.length === 0) {
          setNotificationStatus("warning");
          setNotificationMessage(response?.message || "Volume não encontrado.");
          if (inputRefVolume.current) {
            inputRefVolume.current.focus();
          }
          return;
        }

        const volume = volumes[0];
        const expectedVolume = expectedVolumesData?.find(
          (item) => String(item.id) === String(volume.id),
        );
        if (
          isVolumeAttended(volume.status) ||
          isVolumeAttended(expectedVolume?.status)
        ) {
          setNumberVolumeInput("");
          setNotificationStatus("warning");
          setNotificationMessage(
            "Este volume já está totalmente atendido e não pode ser adicionado.",
          );
          if (inputRefVolume.current) {
            inputRefVolume.current.focus();
          }
          return;
        }

        const alreadyReceived = shipmentReceivedVolumes.some(
          (item) => String(item.id) === String(volume.id),
        );

        if (alreadyReceived) {
          setNumberVolumeInput("");
          setNotificationStatus("warning");
          setNotificationMessage("Volume já consta na lista.");
          if (inputRefVolume.current) {
            inputRefVolume.current.focus();
          }
          return;
        }

        setNumberVolumeInput("");
        setVolumeValidation({ open: true, volume });
      } catch (error) {
        if (inputRefVolume.current) {
          inputRefVolume.current.focus();
        }
        console.error("Erro ao buscar volume:", error);
      } finally {
        hideLoader();
      }
    },
    [
      baseServicePost,
      showLoader,
      hideLoader,
      setNotificationMessage,
      setNotificationStatus,
      shipmentReceivedVolumes,
      expectedVolumesData,
    ],
  );

  const handleValidatedVolume = useCallback(
    (volume, items) => {
      if (!volume) return;

      const hasItems = Array.isArray(items) && items.length > 0;

      if (hasItems) {
        const receivedVolume = {
          id: volume.id,
          description: `${volume.code || "-"} - ${volume.name || "-"}`,
          movement_id: volume.movement_id,
          items,
        };

        const alreadyReceived = shipmentReceivedVolumes.some(
          (item) => String(item.id) === String(volume.id),
        );

        if (!alreadyReceived) {
          const newList = [...shipmentReceivedVolumes, receivedVolume];
          saveReceivedVolumesToLocalStorage(newList);
          setShipmentReceivedVolumes(newList);
        }
      }

      setNumberVolumeInput("");
      setVolumeValidation({ open: false, volume: null });

      // A validação pode ter removido itens não bipados do volume. Atualiza as
      // consultas que exibem itens/status para refletir a requisição.
      queryClient.invalidateQueries({ queryKey: ["expectedVolumes"] });
      queryClient.invalidateQueries({ queryKey: ["movementDetails"] });
      queryClient.invalidateQueries({ queryKey: ["movementItems"] });
      queryClient.invalidateQueries({ queryKey: ["movements"] });
      queryClient.invalidateQueries({ queryKey: ["volumeItems"] });
      queryClient.invalidateQueries({ queryKey: ["volumeValidationItems"] });

      if (inputRefVolume.current) {
        setTimeout(() => inputRefVolume.current?.focus(), 0);
      }
    },
    [
      queryClient,
      saveReceivedVolumesToLocalStorage,
      shipmentReceivedVolumes,
    ],
  );

  const handleCloseVolumeValidation = useCallback(() => {
    setVolumeValidation({ open: false, volume: null });
    setNumberVolumeInput("");

    if (inputRefVolume.current) {
      setTimeout(() => inputRefVolume.current?.focus(), 0);
    }
  }, []);

  const handleDebouncedAddVolume = async (value) => {
    await addVolumeToReceived(value);
  };

  const removeFromReceivedVolume = useCallback(
    (idToRemove) => {
      setShipmentReceivedVolumes((prevList) => {
        const newList = prevList.filter((item) => item.id !== idToRemove);
        saveReceivedVolumesToLocalStorage(newList);
        return newList;
      });

      setNotificationStatus("warning");
      setNotificationMessage("Volume removido da lista.");

      // Para focar no input
      setTimeout(() => {
        const input = document.querySelector('input[name="numberVolumeInput"]');
        if (input) {
          input.focus();
        }
      }, 0);
    },
    [saveReceivedVolumesToLocalStorage],
  );

  // Efeito para setar o movimento caso tenha vindo da URL
  useEffect(() => {
    if (movementRef.current && movementSelect.options.length > 0) {
      const selectedId = Number(movementRef.current);

      const optionExists = movementSelect.options.find(
        (opt) => Number(opt.value) === selectedId,
      );

      if (optionExists) {
        setFormData((prev) => ({
          ...prev,
          movement: {
            ...prev.movement,
            id: [optionExists],
          },
        }));
        movementRef.current = null;
      }
    }
  }, [movementSelect.options, setFormData]);

  const volumeAttended = expectedVolumesData?.every(
    (item) => isVolumeAttended(item.status),
  );

  // Volumes já totalmente atendidos pertencem a carregamentos anteriores e
  // não devem aparecer como opções disponíveis para um novo carregamento.
  // Mantemos expectedVolumesData completo para continuar detectando bipagens duplicadas.
  const availableVolumesData = (expectedVolumesData ?? []).filter(
    (item) => !isVolumeAttended(item.status),
  );

  const allItemsAttended = volumeAttended;

  const title = addVolumeShipmentId
    ? "Adicionar Volumes"
    : "Validar Carregamento";
  const textSubmit = addVolumeShipmentId ? "Adicionar Volumes" : "Validar";
  const textLoadingSubmit = addVolumeShipmentId
    ? "Adicionando..."
    : "Validando...";
  const backUrl = addVolumeShipmentId
    ? `/aluguel/carregamentos/${addVolumeShipmentId}/detalhes`
    : "/aluguel/carregamentos";

  const handleBack = useCallback(() => {
    navigate(backUrl);
  }, [backUrl, navigate]);

  return (
    <MainLayout selectedCompany="ALUCOM">
      <PageHeader title={title} showBackButton={true} backUrl={backUrl} />

      <div className="container-fluid p-1">
        <Form
          initialFormData={formData}
          onSubmit={handleSubmit}
          textSubmit={textSubmit}
          textLoadingSubmit={textLoadingSubmit}
          handleBack={handleBack}
          isSubmitting={isLoading}
          disabledSubmitButton={allItemsAttended}>
          {() => (
            <>
              <div key={dynamicSection.section}>
                <SectionHeader section={dynamicSection.section} />

                <Box>
                  <SimpleBody
                    fields={dynamicSection.fields.map((field) => {
                      if (field.id === "movement_id") {
                        return {
                          ...field,
                          disabled: !!movementIdFromUrl, // Trava o campo se veio da URL
                        };
                      }
                      return field;
                    })}
                    formErrors={normalizeFormErrors(formErrors)}
                    formData={formData}
                    handleFieldChange={handleChange}
                    getOptions={getOptions}
                    getSelectedValue={getSelectedValue}
                  />

                  <Box sx={{ display: "flex", mt: 2 }}>
                    <ValidateShipmentsInput
                      ref={inputRefVolume}
                      value={numberVolumeInput}
                      onChange={setNumberVolumeInput}
                      onDebouncedChange={handleDebouncedAddVolume}
                      isSearching={isLoading}
                      name="numberVolumeInput"
                      label="Código"
                      helperText="Escaneie ou digite o código do volume para conferir os equipamentos"
                    />
                  </Box>
                  <Grid container spacing={2} size="small">
                    <Grid item size={6}>
                      <ValidateShipmentTable
                        title="Volumes dos movimentos"
                        items={
                          movementSelect.selectedValue.length === 0
                            ? []
                            : availableVolumesData
                        }
                        comparisonItems={shipmentReceivedVolumes}
                        showStatusIcon={true}
                        showDetailsButton={true}
                        filterDescription={filterDescriptionVolume}
                        setFilterDescription={setFilterDescriptionVolume}
                        type="expected"
                        showSerial={false}
                      />
                    </Grid>
                    <Grid item size={6}>
                      <ValidateShipmentTable
                        title="Volumes Verificados"
                        items={
                          movementSelect.selectedValue.length === 0
                            ? []
                            : shipmentReceivedVolumes
                        }
                        comparisonItems={expectedVolumesData}
                        showStatusIcon={true}
                        showRemoveButton={true}
                        onRemoveItem={removeFromReceivedVolume}
                        filterDescription={filterDescriptionVolume}
                        setFilterDescription={setFilterDescriptionVolume}
                        type="received"
                        showSerial={false}
                      />
                    </Grid>
                  </Grid>
                </Box>
              </div>
            </>
          )}
        </Form>

        <VolumeValidationModal
          open={volumeValidation.open}
          volume={volumeValidation.volume}
          onClose={handleCloseVolumeValidation}
          onValidated={handleValidatedVolume}
        />

<RomaneioModal
          open={romaneioModalOpen}
          shipmentId={createdShipmentId}
          movementId={selectedMovementIds?.[0]}
          movementIds={selectedMovementIds}
          receivedVolumes={shipmentReceivedVolumes}
          pendingShipmentData={pendingShipmentData}
          createShipmentMutation={createShipmentMutation}
          onClose={() => {
            setRomaneioModalOpen(false)
            setPendingShipmentData(null)
            if (!createdShipmentId) {
              clearReceivedVolumesFromLocalStorage()
            }
            navigate("/aluguel/carregamentos", { replace: true })
          }}
          onSuccess={(shipmentId) => {
            setRomaneioModalOpen(false)
            setPendingShipmentData(null)
            clearReceivedVolumesFromLocalStorage()
            navigate("/aluguel/carregamentos", { replace: true })
          }}
        />
      </div>
    </MainLayout>
  );
};

export default ValidateShipmentsPage;
